import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ILegalRequirementTemplate,
  ILegalRequirementTemplateFullObject,
} from '../Interfaces/ILegalRequirementTemplate';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { useSelector } from 'react-redux';
import { State } from '../redux/mainReducer';
import { TextModuleSelector } from '../TextModule/TextModuleSelector';
import { CustomEditor } from '../core/CustomEditor';
import { ICourtCourtRole } from '../Interfaces/IAemter';
import { useFetch } from '../hooks/useFetch';
import { LegalRequirementAddCourt } from './LegalRequirementAddCourt';
import { Edit } from '@mui/icons-material';
import { ILegalPhaseTemplateFullObject } from '../Interfaces/ILegalPhaseTemplate';
import { ILegalRequirementTemplateDeadline } from '../Interfaces/ILegalRequirementTemplate_deadlines';
import { AddPrerequisiteDeadline } from './AddPrerequisiteDeadline';
import { LegalRequirementDeadlineTable } from './LegalRequirementDeadlineTable';
import { DocumentOverview } from '../Document/DocumentOverview';
import { IDocument } from '../Interfaces/IDocument';

interface IProps {
  currentRequirementTemplate: ILegalRequirementTemplateFullObject;
  setCurrentRequirementTemplate: Function;
  legalRequirementTemplates: ILegalRequirementTemplateFullObject[];
  legalPhaseTemplateArray?: ILegalPhaseTemplateFullObject[];
}

export const EditLegalRequirementTemplateData: React.FC<IProps> = ({
  currentRequirementTemplate,
  setCurrentRequirementTemplate,
  legalRequirementTemplates,
  legalPhaseTemplateArray,
}) => {
  const isSendEmailPossible =
    currentRequirementTemplate.sendMail === true ||
    currentRequirementTemplate.isInterneLegalRequirement === true;

  useEffect(() => {
    const updatedArray = currentRequirementTemplate.DocumentArray
      ? currentRequirementTemplate.DocumentArray.map(doc => ({
          ...doc,
          perEmail: isSendEmailPossible ? doc.perEmail : false,
        }))
      : [];

    setCurrentDocumentArray(updatedArray);
  }, [isSendEmailPossible]);

  const legalRequirementStateArray = useSelector(
    (state: State) =>
      state.legalRequirementStateArray.storeLegalRequirementState
  );

  const [courtcourtRoleArray, , wasSuccessfullyLoadCourt] =
    useFetch<ICourtCourtRole[]>('/courtcourtrole');

  const [courtcourtroleDialogOpen, setCourtcourtroleDialogOpen] =
    useState(false);

  const [currentDocumentArray, setCurrentDocumentArray] = useState<IDocument[]>(
    currentRequirementTemplate.DocumentArray
  );

  useEffect(() => {
    setCurrentRequirementTemplate({
      ...currentRequirementTemplate,
      DocumentArray: currentDocumentArray,
    });
  }, [currentDocumentArray]);

  const [addPrerequisteDeadlineOpen, setAddPrerequisteDeadlineOpen] =
    useState(false);

  const getCourtCourtRoleCurrentProcessingTime = useCallback(
    (idCourt: number, idCourtRole: number) => {
      if (courtcourtRoleArray === undefined) return null;
      const courtCourtRole = courtcourtRoleArray.find(
        x => x.idCourt === idCourt && x.idCourtRole === idCourtRole
      );
      return courtCourtRole?.currentProcessingTime
        ? courtCourtRole.currentProcessingTime
        : courtCourtRole?.initalProcessingTime;
    },
    [courtcourtRoleArray]
  );

  const handleChangePrerequisteDeadline = useCallback(
    (changedPrequiste: ILegalRequirementTemplateDeadline) => {
      const newArray = currentRequirementTemplate.DeadlineArray
        ? currentRequirementTemplate.DeadlineArray
        : [];

      if (changedPrequiste.idDeadline === -1) {
        changedPrequiste.idDeadline = Math.floor(Math.random() * 1000000);
        newArray.push(changedPrequiste);
      } else {
        const index = newArray.findIndex(
          x => x.idDeadline === changedPrequiste.idDeadline
        );
        if (index === -1) {
          newArray.push(changedPrequiste);
        } else {
          newArray[index] = changedPrequiste;
        }
      }
      setCurrentRequirementTemplate({
        ...currentRequirementTemplate,
        DeadlineArray: newArray,
      });
    },
    [currentRequirementTemplate]
  );

  const handleDeleteDeadline = useCallback(
    (index: number) => {
      const newArray = currentRequirementTemplate.DeadlineArray
        ? currentRequirementTemplate.DeadlineArray
        : [];
      newArray.splice(index, 1);
      setCurrentRequirementTemplate({
        ...currentRequirementTemplate,
        DeadlineArray: newArray,
      });
    },
    [currentRequirementTemplate]
  );

  if (!wasSuccessfullyLoadCourt || !wasSuccessfullyLoadCourt) {
    return <div>Fehler beim Laden der Daten</div>;
  } else if (courtcourtRoleArray === undefined) {
    return (
      <div>
        <CircularProgress></CircularProgress>
      </div>
    );
  }
  return (
    <Box sx={{ m: 2 }}>
      <Grid container spacing={2}>
        <Grid item sm={2}>
          <TextField
            label='interne Maßnahme'
            value={
              currentRequirementTemplate.isInterneLegalRequirement === true
                ? 'true'
                : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                isInterneLegalRequirement: event.target.value === 'true',
                sendMail:
                  event.target.value === 'true'
                    ? false
                    : currentRequirementTemplate.sendMail,
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>{' '}
          </TextField>
        </Grid>
        <Grid item sm={10}>
          <TextField
            label='Bedingung'
            value={currentRequirementTemplate.LegalRequirementTemplate}
            error={currentRequirementTemplate.LegalRequirementTemplate === ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                LegalRequirementTemplate: event.target.value,
              })
            }
            size='small'
            fullWidth
          />
        </Grid>

        <Grid item sm={6}>
          <TextField
            label='Mitarbeiter kann Dateien hochladen'
            value={
              currentRequirementTemplate.canUploadFilesEmployees === true
                ? 'true'
                : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                canUploadFilesEmployees: event.target.value === 'true',
                DocumentArray:
                  event.target.value === 'false'
                    ? []
                    : currentRequirementTemplate.DocumentArray,
              });
              setCurrentDocumentArray(
                event.target.value === 'false' ? [] : currentDocumentArray
              );
            }}
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
        <Grid item sm={6}>
          <TextField
            label='Mandant kann Dateien hochladen'
            value={
              currentRequirementTemplate.canUploadFilesClient === true
                ? 'true'
                : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                canUploadFilesClient: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>

        <Grid item sm={5}>
          <TextField
            label='Startstatus'
            value={currentRequirementTemplate.idLegalRequirementState}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                idLegalRequirementState: Number(event.target.value),
              })
            }
            size='small'
            fullWidth
            select
          >
            {legalRequirementStateArray.map(x => (
              <MenuItem
                key={`idLegalRequirementState-${x.idLegalRequirementState}`}
                value={x.idLegalRequirementState}
              >
                {x.LegalRequirementState}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item sm={3}>
          <TextField
            label='Vorlage im MA-Katalog'
            disabled={currentRequirementTemplate.idLegalPhaseTemplate === null}
            value={
              currentRequirementTemplate.isTemplate === true ? 'true' : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                isTemplate: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>

        <Grid item sm={4}>
          <TextField
            label='Vorgänger (Bedingung)'
            value={
              currentRequirementTemplate.Predecessor_idLegalRequirementTemplate ===
              undefined
                ? ''
                : currentRequirementTemplate.Predecessor_idLegalRequirementTemplate
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                Predecessor_idLegalRequirementTemplate: Number(
                  event.target.value
                ),
              })
            }
            size='small'
            fullWidth
            select
          >
            {legalRequirementTemplates.map(x => (
              <MenuItem
                key={`predecessor-idLegalRequirementTemplate-${x.idLegalRequirementTemplate}`}
                value={x.idLegalRequirementTemplate}
              >
                {x.LegalRequirementTemplate}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {currentRequirementTemplate.canUploadFilesEmployees === true && (
          <Grid mt={6} item sm={12}>
            <DocumentOverview
              documentArray={currentDocumentArray}
              setDocumentArray={setCurrentDocumentArray}
              allowUpload={true}
              allowRemove={true}
              title={'Standarddokumente'}
              isStandarddocument={true}
              isTemplate
              isSendEmailPossible={isSendEmailPossible}
            />
          </Grid>
        )}

        <Grid item sm={12}>
          <Box
            component='fieldset'
            sx={{
              borderColor: '#ccc',
              border: 1,
              borderRadius: 4,
              padding: 2,
              marginTop: 4,
              position: 'relative',
            }}
          >
            <Typography
              component='legend'
              sx={{ fontSize: '0.875rem', padding: '0 8px' }}
            >
              Fristen und Gerichte
            </Typography>
            <Grid container spacing={2} justifyContent='center'>
              {currentRequirementTemplate.idCourt === null &&
                currentRequirementTemplate.idCourtRole === null && (
                  <>
                    <Grid
                      item
                      xs={12}
                      display={'flex'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                    >
                      <Tooltip title='Die geschätzte Bearbeitungszeit ist die geschätzte Zeit für den Abschluss dieser Maßnahme unter normalen Umständen. Sollten Sie die geschätzte Bearbeitungszeit ändern, wird nach dem Speichern eine neue Kalkulation der berechneten Bearbeitungszeit auf Grundlage der historischen Daten und der geschätzen Bearbeitungszeit durchgeführt. Sie sehen das neue Ergebnis, wenn Sie diese Maßnahme erneut laden'>
                        <TextField
                          label='Geschätzte Bearbeitungszeit'
                          type='number'
                          value={
                            currentRequirementTemplate.initialProcessingTime
                              ? currentRequirementTemplate.initialProcessingTime
                              : ''
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setCurrentRequirementTemplate({
                              ...currentRequirementTemplate,
                              initialProcessingTime: Number(event.target.value),
                            })
                          }
                          size='small'
                          sx={{ width: '50%' }}
                        />
                      </Tooltip>
                      <Tooltip title='Die berechnete Bearbeitungszeit ist die durchschnittliche Zeit, die für die Bearbeitung dieser Maßnahme benötigt wird. Die Berechnung basiert auf den Daten bereits abgeschlossener Maßnahmen. Sollten noch keine Daten vorliegen, wird die geschätzte Bearbeitungszeit angezeigt.'>
                        <Typography
                          variant='body1'
                          color='text.secondary'
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            textAlign: 'right',
                          }}
                        >
                          Berechnete Bearbeitungszeit:{' '}
                          {currentRequirementTemplate.currentProcessingTime !==
                          null
                            ? `${currentRequirementTemplate.currentProcessingTime} Tage`
                            : currentRequirementTemplate.initialProcessingTime !==
                                null
                              ? `${currentRequirementTemplate.initialProcessingTime} Tage`
                              : 'noch nicht festgelegt'}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    ></Grid>
                    <Tooltip title='Alternativ können Sie auch ein zuständiges Amt für diese Maßnahme festlegen. Die Bearbeitungszeit wird dann automatisch basierend auf den Daten des zuständigen Amts kaluliert.'>
                      <Button
                        variant='contained'
                        color='primary'
                        sx={{ mt: 1 }}
                        onClick={() => {
                          setCourtcourtroleDialogOpen(true);
                        }}
                      >
                        Zuständiges Amt festlegen
                      </Button>
                    </Tooltip>
                  </>
                )}
              <Grid item xs={12}>
                <Box>
                  {currentRequirementTemplate.idCourt !== null &&
                    currentRequirementTemplate.idCourtRole !== null && (
                      <>
                        <Grid
                          item
                          xs={12}
                          display={'flex'}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                        >
                          <Tooltip title='Das Amt, welches für die Bearbeitung dieser Maßnahme zuständig ist.'>
                            <Typography variant='body1'>
                              Amt: {currentRequirementTemplate.CourtRole}{' '}
                              {currentRequirementTemplate.Court}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          display={'flex'}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                        >
                          <Tooltip title='Die berechnete Bearbeitungszeit ist die durchschnittliche Zeit, die für die Bearbeitung durch das zuständige Amt benötigt wird. Die Berechnung basiert auf den Daten bereits abgeschlossener Maßnahmen. Sollten noch keine Daten vorliegen, wird die geschätzte Bearbeitungszeit des Gerichts angezeigt.'>
                            <Typography variant='subtitle1'>
                              Berechnete Bearbeitungszeit:{' '}
                              {getCourtCourtRoleCurrentProcessingTime(
                                currentRequirementTemplate.idCourt,
                                currentRequirementTemplate.idCourtRole
                              )}{' '}
                              Tage
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Box display='flex' gap={2}>
                          <Tooltip title='Ändern Sie die Zuweisung zu einem anderen Amt'>
                            <Button
                              variant='contained'
                              color='primary'
                              sx={{ mt: 1 }}
                              onClick={() => {
                                setCourtcourtroleDialogOpen(true);
                              }}
                            >
                              Zuweisung ändern
                            </Button>
                          </Tooltip>
                          <Tooltip title='Alternativ können Sie auch die Zuweisung zu einem Amt löschen. Die Bearbeitungszeit wird dann basierend auf historischen Daten der Maßnahme berechnet.'>
                            <Button
                              variant='contained'
                              color='warning'
                              sx={{ mt: 1 }}
                              onClick={() => {
                                setCurrentRequirementTemplate({
                                  ...currentRequirementTemplate,
                                  idCourt: null,
                                  idCourtRole: null,
                                  Court: null,
                                  CourtRole: null,
                                });
                              }}
                            >
                              Zuweisung löschen
                            </Button>
                          </Tooltip>
                        </Box>
                      </>
                    )}

                  <Divider>
                    <Typography variant='subtitle1'>
                      Startbedingungen
                    </Typography>
                  </Divider>
                  <Grid item xs={12} display={'flex'} justifyContent={'right'}>
                    <Tooltip title='Bearbeiten Sie die Startbedingungen'>
                      <IconButton
                        onClick={() => setAddPrerequisteDeadlineOpen(true)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <LegalRequirementDeadlineTable
                    deadlineArray={currentRequirementTemplate.DeadlineArray}
                    setDeadline={handleChangePrerequisteDeadline}
                    deleteDeadline={handleDeleteDeadline}
                  ></LegalRequirementDeadlineTable>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item sm={12}>
          <TextModuleSelector
            key={`type-legalrequirementedit`}
            floatRight
            idTextModuleCategory={4}
            txt={currentRequirementTemplate.InfoText}
            setTxt={(txt: string) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                InfoText: txt,
              })
            }
          />
          <TextField
            label='Infotext'
            value={
              currentRequirementTemplate.InfoText === null
                ? ''
                : currentRequirementTemplate.InfoText
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                InfoText: event.target.value === '' ? null : event.target.value,
              })
            }
            size='small'
            fullWidth
            multiline
            rows={3}
          />
        </Grid>

        <Grid item sm={4}>
          <TextField
            label='Auto. E-Mail versenden (wenn erfüllt)'
            value={
              currentRequirementTemplate.sendMail === true ? 'true' : 'false'
            }
            disabled={
              currentRequirementTemplate.isInterneLegalRequirement === true
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                sendMail: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>

        <Grid item sm={8}>
          <TextField
            label='E-Mail Betreff'
            value={currentRequirementTemplate.EmailSubject}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                EmailSubject: event.target.value,
              })
            }
            size='small'
            fullWidth
          />
        </Grid>

        <CustomEditor
          txt={
            currentRequirementTemplate.EmailBody === null
              ? ''
              : currentRequirementTemplate.EmailBody
          }
          setTxt={(txt: string) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              EmailBody: txt === '' ? null : txt,
            })
          }
          idTextModuleCategory={3}
          dictonaryIdsArray={[1, 2, 3]}
        />

        <Grid item sm={6}>
          <TextField
            label='Fehler möglich'
            value={
              currentRequirementTemplate.hasError === true ? 'true' : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                hasError: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
        <Grid item sm={6}>
          <TextField
            label='Freitext bei Fehler erlauben'
            value={
              currentRequirementTemplate.hasErrorFreeText === true
                ? 'true'
                : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                hasErrorFreeText: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>

        <Grid item sm={12}>
          <TextField
            label='Fehlermeldung'
            value={currentRequirementTemplate.TextOfError}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                TextOfError: event.target.value,
              })
            }
            size='small'
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
      <LegalRequirementAddCourt
        legalRequirement={currentRequirementTemplate}
        courtCourtRoleArray={courtcourtRoleArray}
        open={courtcourtroleDialogOpen}
        onSave={(newLegalRequirement: ILegalRequirementTemplate) => {
          setCurrentRequirementTemplate(newLegalRequirement);
          setCourtcourtroleDialogOpen(false);
        }}
        onClose={() => setCourtcourtroleDialogOpen(false)}
      />
      <AddPrerequisiteDeadline
        open={addPrerequisteDeadlineOpen}
        currentLegalRequirementTemplate={currentRequirementTemplate}
        legalRequirementTemplateArray={legalRequirementTemplates}
        setDeadline={handleChangePrerequisteDeadline}
        onClose={() => setAddPrerequisteDeadlineOpen(false)}
        legalPhaseTemplateArray={legalPhaseTemplateArray}
      />
    </Box>
  );
};
